// Browser native speech synthesis (pt-BR) for calling patients on the TV.
let voicesCache = [];

function loadVoices() {
  if (typeof window === "undefined" || !window.speechSynthesis) return [];
  voicesCache = window.speechSynthesis.getVoices() || [];
  return voicesCache;
}

if (typeof window !== "undefined" && window.speechSynthesis) {
  loadVoices();
  window.speechSynthesis.onvoiceschanged = loadVoices;
}

export const VOICE_LANGS = { "pt-BR": "pt-BR", es: "es-MX", en: "en-US", fr: "fr-FR", it: "it-IT", de: "de-DE" };

function pickVoice(bcp) {
  const voices = voicesCache.length ? voicesCache : loadVoices();
  const norm = (v) => (v.lang || "").toLowerCase().replace("_", "-");
  const want = bcp.toLowerCase();
  const base = want.split("-")[0];
  return (
    voices.find((v) => norm(v) === want) ||
    (base === "pt" ? null : voices.find((v) => norm(v).startsWith(base))) ||
    null
  );
}

export function speechSupported() {
  return typeof window !== "undefined" && !!window.speechSynthesis;
}

let speakToken = 0;

// Speaks text. Retries once with the system default voice if the first try fails or never starts.
// onError(code) reports the last failure ("not-allowed" = needs a tap on the TV).
export function speakCall(text, { onStart, onEnd, onError, rate = 0.92, lang = "pt-BR" } = {}) {
  const bcp = VOICE_LANGS[lang] || "pt-BR";
  const synth = speechSupported() ? window.speechSynthesis : null;
  if (!synth) { if (onError) onError("unsupported"); if (onEnd) onEnd(); return; }
  const token = ++speakToken;
  let finished = false;
  const finish = () => {
    if (finished || token !== speakToken) return;
    finished = true;
    if (onEnd) onEnd();
  };
  const attempt = (retry) => {
    if (token !== speakToken) return;
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = bcp;
    utter.rate = rate;
    utter.pitch = 1;
    utter.volume = 1;
    const voice = retry ? null : pickVoice(bcp);
    if (voice) utter.voice = voice;
    let started = false;
    let failedOver = false;
    const failover = (code) => {
      if (failedOver || finished) return;
      failedOver = true;
      if (!retry && code !== "not-allowed") {
        try { synth.cancel(); } catch (e) { /* ignore */ }
        setTimeout(() => attempt(true), 200);
        return;
      }
      if (onError) onError(code);
      finish();
    };
    utter.onstart = () => { started = true; if (onStart) onStart(); };
    utter.onend = () => finish();
    utter.onerror = (e) => failover((e && e.error) || "error");
    window.__lastUtterance = utter; // keep a reference (Chrome may garbage-collect it mid-speech)
    try {
      if (synth.paused) synth.resume();
      synth.speak(utter);
    } catch (e) { failover("exception"); return; }
    setTimeout(() => { if (!started && !finished && !synth.speaking) failover("timeout"); }, 3000);
  };
  try { if (synth.speaking || synth.pending) synth.cancel(); } catch (e) { /* ignore */ }
  setTimeout(() => attempt(false), 150);
}

// Two-tone "ding-dong" rendered once as a WAV and played with a normal <audio> element
// (same path as the TV videos, so it never competes with the voice for the audio output).
let chimeAudio = null;

function buildChimeUrl() {
  const rate = 22050;
  const len = Math.floor(rate * 1.2);
  const data = new Int16Array(len);
  const tones = [[880, 0], [660, 0.45]];
  for (let i = 0; i < len; i++) {
    const t = i / rate;
    let s = 0;
    for (const [f, start] of tones) {
      const dt = t - start;
      if (dt >= 0) s += Math.sin(2 * Math.PI * f * dt) * Math.exp(-dt * 5) * Math.min(1, dt * 60);
    }
    data[i] = Math.max(-1, Math.min(1, s * 0.45)) * 32767;
  }
  const buf = new ArrayBuffer(44 + len * 2);
  const v = new DataView(buf);
  const w = (o, str) => { for (let i = 0; i < str.length; i++) v.setUint8(o + i, str.charCodeAt(i)); };
  w(0, "RIFF"); v.setUint32(4, 36 + len * 2, true); w(8, "WAVE"); w(12, "fmt ");
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true);
  v.setUint16(34, 16, true); w(36, "data"); v.setUint32(40, len * 2, true);
  new Int16Array(buf, 44).set(data);
  return URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
}

function getChime() {
  if (!chimeAudio && typeof Audio !== "undefined") {
    chimeAudio = new Audio(buildChimeUrl());
    chimeAudio.preload = "auto";
  }
  return chimeAudio;
}

export function playChime(volume = 0.8) {
  return new Promise((resolve) => {
    const a = getChime();
    if (!a) return resolve();
    let done = false;
    const end = () => { if (!done) { done = true; resolve(); } };
    a.onended = () => setTimeout(end, 150);
    try {
      a.pause();
      a.currentTime = 0;
      a.muted = false;
      a.volume = volume;
      const p = a.play();
      if (p && p.catch) p.catch(end);
    } catch (e) { end(); }
    setTimeout(end, 1800);
  });
}

let voiceEl = null;
function getVoiceEl() {
  if (!voiceEl && typeof Audio !== "undefined") {
    voiceEl = new Audio();
    voiceEl.preload = "auto";
  }
  return voiceEl;
}

// Plays a server-generated voice file (works on TVs without a built-in voice, e.g. LG webOS).
// Resolves true when it played, false on any failure.
export function playVoiceUrl(url, volume = 1) {
  return new Promise((resolve) => {
    const a = getVoiceEl();
    if (!a) return resolve(false);
    let done = false;
    let started = false;
    let guard = null;
    const end = (ok) => { if (!done) { done = true; clearTimeout(guard); if (!ok) { try { a.pause(); } catch (e) { /* ignore */ } } resolve(ok); } };
    a.onplaying = () => { started = true; };
    a.onended = () => end(true);
    a.onerror = () => end(false);
    guard = setTimeout(() => end(started), 20000);
    try {
      a.pause();
      a.muted = false;
      a.volume = volume;
      a.src = url;
      const p = a.play();
      if (p && p.catch) p.catch(() => end(false));
    } catch (e) { end(false); }
    setTimeout(() => { if (!started) end(false); }, 9000);
  });
}

// Must run inside a user gesture (click / remote OK) to unlock voice and chime on TVs.
export function primeAudio() {
  try {
    const v = getVoiceEl();
    if (v && !v.src) v.src = getChime().src;
    if (v) {
      v.muted = true;
      const p = v.play();
      const stop = () => { v.pause(); v.muted = false; };
      if (p && p.then) p.then(stop).catch(() => { v.muted = false; });
      else stop();
    }
  } catch (e) { /* ignore */ }
  try {
    if (window.speechSynthesis) {
      const u = new SpeechSynthesisUtterance(" ");
      u.volume = 0;
      window.speechSynthesis.speak(u);
    }
  } catch (e) { /* ignore */ }
  try {
    const a = getChime();
    if (a) {
      a.muted = true;
      const p = a.play();
      const stop = () => { a.pause(); a.currentTime = 0; a.muted = false; };
      if (p && p.then) p.then(stop).catch(() => { a.muted = false; });
      else stop();
    }
  } catch (e) { /* ignore */ }
}

// Only a real tap/keypress unlocks the voice (speechSynthesis), so require user activation.
export function audioAlreadyAllowed() {
  try {
    return !!(navigator.userActivation && navigator.userActivation.hasBeenActive);
  } catch (e) {
    return false;
  }
}

export function buildCallText(name, roomName) {
  const parts = [];
  if (name) parts.push(name);
  if (roomName) parts.push(`${name ? ", compareça ao " : "Senha chamando no "}${roomName}`);
  return parts.length ? parts.join("") : "Próximo paciente";
}
